import { formatCommentAuthorLabel } from './adminCommentDisplay.js';

/** JSONB from API is usually an object; legacy rows may store a JSON string. */
export function parseMetaObject(raw) {
  if (raw == null || raw === '') return {};
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return {};
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
    } catch {
      return {};
    }
    return {};
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  return {};
}

function pickMetaString(meta, keys) {
  for (const k of keys) {
    const v = meta?.[k];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return '';
}

/** Parse admin_comment_log whether JSONB array, JSON string, or plain text. */
export function normalizeAdminCommentLog(raw) {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
      if (parsed && typeof parsed === 'object') return [parsed];
    } catch {
      return [{ text: trimmed }];
    }
    return [{ text: trimmed }];
  }
  if (typeof raw === 'object') return [raw];
  return [];
}

export function commentTextFromEntry(entry) {
  if (entry == null) return '';
  if (typeof entry === 'string') return entry.trim();
  return String(entry?.text ?? entry?.message ?? entry?.note ?? entry?.comment ?? '').trim();
}

export function commentWhenFromEntry(entry) {
  if (!entry || typeof entry !== 'object') return null;
  return entry?.at || entry?.createdAt || entry?.created_at || null;
}

function authorFromStatusEvent(ev) {
  const user = ev?.createdByUser || ev?.user || null;
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  if (name) return name;
  return formatCommentAuthorLabel(ev) || 'Admin';
}

/**
 * Read-only admin comment lines: { kind, text, author, when }.
 * Legacy line(s) first; newer admin / status-event entries sorted by time desc.
 */
export function buildAdminCommentDisplayLines({
  legacyText,
  legacyAuthor = 'Registration note',
  legacyWhen = null,
  log,
  statusEvents = [],
  dedupe = true
}) {
  const lines = [];
  const seen = new Set();

  const push = (line) => {
    const text = String(line.text || '').trim();
    if (!text) return;
    const key = dedupe ? `${text.toLowerCase()}::${line.when || ''}::${line.author || ''}` : null;
    if (key && seen.has(key)) return;
    if (key) seen.add(key);
    lines.push(line);
  };

  const legacy = String(legacyText || '').trim();
  if (legacy) {
    push({ kind: 'legacy', text: legacy, author: legacyAuthor, when: legacyWhen });
  }

  const timed = [];

  for (const entry of normalizeAdminCommentLog(log)) {
    const text = commentTextFromEntry(entry);
    if (!text) continue;
    timed.push({
      kind: 'admin',
      text,
      author: formatCommentAuthorLabel(entry),
      when: commentWhenFromEntry(entry)
    });
  }

  for (const ev of statusEvents || []) {
    const text = String(ev?.comment ?? '').trim();
    if (!text) continue;
    timed.push({
      kind: 'status_event',
      text,
      author: authorFromStatusEvent(ev),
      when: ev?.createdAt || ev?.created_at || null
    });
  }

  timed.sort((a, b) => new Date(b.when || 0).getTime() - new Date(a.when || 0).getTime());
  for (const line of timed) push(line);

  return lines;
}

/** Registration / booking admin + legacy Hasura registration comment. */
export function buildRegistrationCommentLines(booking) {
  const reg = booking?.registration || {};
  const meta = parseMetaObject(booking?.meta);
  const legacyText =
    reg?.registrationComment ??
    reg?.comment ??
    meta?.comment ??
    meta?.registration_comment ??
    '';
  const log =
    booking?.adminCommentLog ??
    booking?.admin_comment_log ??
    meta?.admin_comment_log ??
    meta?.adminCommentLog;
  return buildAdminCommentDisplayLines({
    legacyText,
    legacyAuthor: 'Registration note',
    log
  });
}

/** Cancellation request: legacy column, meta, status events, append log. */
export function buildCancellationCommentLines(detail) {
  if (!detail) return [];
  const meta = parseMetaObject(detail?.meta);
  const adminComment = pickMetaString(meta, ['admin_comment', 'adminComment'])
    || String(detail?.adminComment ?? detail?.admin_comment ?? '').trim();
  const metaComment =
    String(detail?.metaComment ?? detail?.meta_comment ?? '').trim()
    || pickMetaString(meta, ['comment', 'notes']);
  const log = meta?.admin_comment_log ?? meta?.adminCommentLog ?? [];
  const statusEvents = detail?.statusEvents || detail?.status_events || [];

  const legacyLines = [];
  if (adminComment) {
    legacyLines.push({
      kind: 'legacy',
      text: adminComment,
      author: 'Legacy admin note',
      when: detail?.updatedAt || detail?.createdAt || null
    });
  }
  if (metaComment && metaComment.toLowerCase() !== adminComment.toLowerCase()) {
    legacyLines.push({
      kind: 'legacy',
      text: metaComment,
      author: 'Admin note (Hasura)',
      when: detail?.requestedAt || detail?.createdAt || null
    });
  }

  const timed = buildAdminCommentDisplayLines({
    legacyText: '',
    log,
    statusEvents
  }).filter((line) => line.kind !== 'legacy');

  return [...legacyLines, ...timed];
}
